import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, Field, PlainSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const CATS = ["osteopathy", "physio", "massage", "aesthetics", "general", "letter"];
const keyOf = (s, i) => (s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 30) || `section_${i}`);

function FormEditor({ tpl, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("general");
  const [sections, setSections] = useState([""]);
  useEffect(() => { if (tpl) { setName(tpl.name || ""); setCategory(tpl.category || "general"); setSections(tpl.sections?.map((s) => s.label) || [""]); } }, [tpl]);
  if (!tpl) return null;
  const save = async () => {
    const body = { name, category, sections: sections.filter(Boolean).map((l, i) => ({ key: keyOf(l, i), label: l })) };
    try { tpl.id ? await api.put(`/note-templates/${tpl.id}`, body) : await api.post("/note-templates", body); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="form-editor">
        <DialogHeader><DialogTitle>{tpl.id ? "Edit form" : "New note form"}</DialogTitle></DialogHeader>
        <Field label="Form name"><Input data-testid="form-name" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Category"><PlainSelect data-testid="form-category" value={category} onChange={(e) => setCategory(e.target.value)}>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</PlainSelect></Field>
        <div className="space-y-2"><div className="text-xs font-semibold text-muted-foreground">Sections</div>
          {sections.map((s, i) => (
            <div key={i} className="flex gap-2"><Input data-testid={`form-section-${i}`} value={s} placeholder="e.g. Treatment given" onChange={(e) => setSections(sections.map((x, j) => (j === i ? e.target.value : x)))} />
              <Button size="icon" variant="ghost" onClick={() => setSections(sections.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button></div>))}
          <Button size="sm" variant="outline" className="rounded-full" data-testid="form-add-section" onClick={() => setSections([...sections, ""])}><Plus className="h-4 w-4 mr-1" />Add section</Button>
        </div>
        <Button className="rounded-full" data-testid="form-save" disabled={!name || !sections.some(Boolean)} onClick={save}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function ConsentLibrary() {
  const { can, reload } = useApp();
  const [lib, setLib] = useState([]);
  const load = useCallback(() => api.get("/consent-library").then((r) => setLib(r.data)), []);
  useEffect(() => { if (can("consents.write")) load(); }, [load, can]);
  if (!can("consents.write")) return null;
  const add = (names) => api.post("/consent-library/import", { names }).then((r) => { toast.success(`${r.data.added} consent forms added`); load(); reload?.(); });
  return (
    <section className="space-y-3" data-testid="consent-library">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-2xl">Standard consent forms</h2><p className="text-sm text-muted-foreground">Ready-made templates. Review and adapt them with your insurer or legal advisor before use. Once added, edit them in Consents.</p></div>
        <Button className="rounded-full" data-testid="consent-library-add-all" onClick={() => add([])}>Add all missing</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {lib.map((c) => (
          <div key={c.name} className="flex items-center gap-2 rounded-lg border bg-card p-3 text-sm" data-testid={`consent-lib-${c.name}`}>
            <span className="flex-1">{c.name}</span>
            {c.added ? <span className="text-xs text-emerald-700 flex items-center gap-1"><Check className="h-3.5 w-3.5" />Added</span>
              : <Button size="sm" variant="outline" className="rounded-full" onClick={() => add([c.name])}>Add</Button>}
          </div>))}
      </div>
    </section>
  );
}

export default function Forms() {
  const [tpls, setTpls] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/note-templates").then((r) => setTpls(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-10">
      <div>
        <PageHeader eyebrow="Clinical documentation" title="Forms & notes"><Button className="rounded-full" data-testid="new-form-btn" onClick={() => setEdit({})}><Plus className="h-4 w-4 mr-1.5" />New form</Button></PageHeader>
        <p className="text-sm text-muted-foreground -mt-4 mb-6">Note forms used for clinical notes and the AI Scribe. Open a patient → Clinical notes to use them.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {tpls.map((t) => (
            <div key={t.id} className="rounded-[var(--radius)] border bg-card p-5 space-y-2" data-testid={`form-${t.id}`}>
              <div className="flex items-center gap-2"><span className="font-semibold flex-1">{t.name}</span>
                <span className="text-[10px] uppercase tracking-wide rounded-full bg-muted px-2 py-0.5">{t.category}</span>
                {t.builtin ? <span className="text-[10px] rounded-full bg-primary/10 text-primary px-2 py-0.5">Built-in</span> : <>
                  <Button size="icon" variant="ghost" data-testid={`edit-form-${t.id}`} onClick={() => setEdit(t)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-form-${t.id}`} onClick={() => window.confirm("Delete this form?") && api.delete(`/note-templates/${t.id}`).then(load)}><Trash2 className="h-4 w-4" /></Button></>}
              </div>
              <ul className="text-xs text-muted-foreground space-y-0.5 list-disc pl-4">{t.sections.map((s) => <li key={s.key}>{s.label.split(" – ")[0]}</li>)}</ul>
            </div>))}
        </div>
      </div>
      <ConsentLibrary />
      <FormEditor tpl={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
